/**
 * 企业模型轮播
 */
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { SVGPureIcon } from '@/ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import tti_icon from '@/assets/img/tti_icon.png';
import Tooltip from '@/ui/tooltip';
import { Image } from 'antd/es';
import Conf from '@/common/conf/index.js';
import ModelManagementPrivateConfirm from '@/components/modal/modal-management/confirm.jsx';
import ModelManagementModal from '@/components/modal/modal-management/index.jsx';
import ModelChoiceModal from '@/components/modal/model-choice/index.jsx';
import modelChoiceStore from '@/components/model-choice/store.js';
import userStore from '@/store/user';
import globalStore from '@/store/global';
import agentPageStore from '@/pages/agent-home/store';
import { NoneKbWarningModal} from '@/components/kb-node-tree/NoneKBWarningModal'

const BusinessSwiper = observer(({ userInfo }) => {
  const swiperRef = useRef(null);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const { pushFrontId, switchCurId, switchTab, activeTab, curId } = modelChoiceStore;
  const [currentModelList, setCurrentModelList] = useState([]);
  const [tooltipStates, setTooltipStates] = useState({});
  const [noKbModalVisible, updateNoKbModalVisible] = useState(false)
  const switchedModelId = useRef('')

  useEffect(() => {
    if (swiperRef.current) {
      const swiperInstance = swiperRef.current.swiper;
      swiperInstance.on('slideChange', () => {
        // 滚动到最后一个slide
        setIsLastSlide(swiperInstance.isEnd);
      });
    }
  }, [swiperRef]);

  useEffect(() => {
    return () => {
      ModelManagementModal.close();
      ModelChoiceModal.close();
      ModelManagementPrivateConfirm.close();
    };
  }, []);

  useEffect(() => {
    if (activeTab === 10) {
      setCurrentModelList(modelChoiceStore.commonList);
    } else if (activeTab === 9999) {
      setCurrentModelList(modelChoiceStore.orgList);
    }
    // 当activeTab变化时，重置swiper到初始位置
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper?.slideTo(0);
    }
  }, [activeTab, modelChoiceStore.orgList, modelChoiceStore.commonList]);

  // 检查文本是否溢出并更新tooltip状态
  useEffect(() => {
    const checkOverflow = () => {
      const titleElements = document.querySelectorAll('.business-swiper-title');
      const newTooltipStates = {};

      titleElements.forEach((element, index) => {
        newTooltipStates[index] = element.scrollWidth > element.clientWidth;
      });

      setTooltipStates(newTooltipStates);
    };

    // 延迟检查，确保DOM已渲染
    const timer = setTimeout(checkOverflow, 100);
    return () => clearTimeout(timer);
  }, [currentModelList]);

  const confirmSwitchModel = () => {
    updateNoKbModalVisible(false)
  }

  const switchModel = () => {
    pushFrontId(switchedModelId.current);
    switchCurId(switchedModelId.current);
  }

  return (
    <div className="max-w-800px min-w-652px w-full mt-44px mb-4px">
      <div className="flex items-center text-black mb-16px">
        <div className="flex items-center gap-24px">
          <div className="flex gap-4px p-4px bg-[rgba(37,45,77,0.05)] rounded-[6px]">
            {modelChoiceStore.tabs.map(item => {
              return (
                <div
                  key={item.type}
                  className={`text-12px py-0 px-16px h-32px flex items-center cursor-pointer ${activeTab === item.id ? 'font-bold bg-white' : ''}`}
                  onClick={() => {
                    switchTab(item.id);
                  }}
                >
                  {item.name}
                </div>
              );
            })}
          </div>
          {activeTab === 10 && (
            <div className="flex items-center gap-4px">
              <img
                src="https://p4.ssl.qhimg.com/t110b9a9301b978c9f79ce51b53.png"
                alt=""
                className="size-16px rounded-50%"
              />
              <div className="text-12px text-[#00A85F] leading-18px">限时免费</div>
            </div>
          )}

          {activeTab === 9999 && (
            <div className="flex items-center gap-4px">
              <div className="flex items-center gap-4px">
                <img
                  src="https://p2.ssl.qhimg.com/t110b9a930189dd83d4cf14d882.png"
                  alt=""
                  className="size-16px rounded-50%"
                />
                <div className="text-12px text-[#676C83] leading-18px">{userInfo.company_name}</div>
              </div>
            </div>
          )}
        </div>
        <div className="ml-auto flex gap-12px">
          <div className="business-swiper-btn-prev size-20px vh-center cursor-pointer transform rotate-[180deg]">
            <SVGPureIcon name="swiper_arrow" className="w-8px h-13px text-black" />
          </div>
          <div className="business-swiper-btn-next size-20px vh-center cursor-pointer">
            <SVGPureIcon name="swiper_arrow" className="w-8px h-13px text-black" />
          </div>
        </div>
      </div>
      <div className="w-full relative overflow-hidden flex items-center">
        <div
          style={{
            background: 'linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)',
          }}
          className={[
            'w-48px h-full absolute right-0 top-0 pointer-events-none z-2',
            isLastSlide ? 'hidden' : '',
          ]}
        ></div>
        <Swiper
          ref={swiperRef}
          className="w-full"
          direction="horizontal"
          slidesPerView={'auto'}
          spaceBetween={12}
          navigation={{
            prevEl: `.business-swiper-btn-prev`,
            nextEl: `.business-swiper-btn-next`,
          }}
          // 触摸灵敏度，值越小越不容易触发滑动，默认为1
          touchRatio={0.8}
          // 允许触发滑动的最大角度(deg)，大于这个角度不会触发滑动
          touchAngle={45}
          // 禁用短距离滑动
          shortSwipes={false}
          // 触发长距离滑动的阈值，滑动距离超过swiper宽度的30%才会触发
          longSwipesRatio={0.3}
          // 触发长距离滑动的最短时间(ms)，小于这个时间不会触发
          longSwipesMs={300}
        >
          {currentModelList.map((item, index) => {
            const isPlaceHolder = item.is_preset;
            const isDeepseek = item.title && /deepseek/i.test(item.title);
            const isFullR1 = isDeepseek && /满血/i.test(item.title);
            const isV3 = isDeepseek && /v3/i.test(item.title);

            return (
              <SwiperSlide key={index} className="!w-150px">
                <Tooltip
                  zIndex={tooltipStates[index] ? 19 : -100}
                  trigger="hover"
                  title={item.home_recommend || item.title}
                >
                  <li
                    className={[
                      'group home-auto-item relative select-none',
                      'flex gap-6px items-center box-border',
                      'w-150px h-72px px-10px pt-18px pb-20px bg-white',
                      'rounded-8px cursor-pointer border-1px',
                      'hover:bg-[#f7f7f7]',
                      curId === item?.id
                        ? `selected ${globalStore.isDark ? 'border-[rgba(255,255,255,0.5)]' : 'border-[rgba(0,0,0,1)] border-2px'}`
                        : 'border-[rgba(0,0,0,0.16)]',
                      isFullR1 || item.id == Conf.roleIds.dynamicPage2 ? 'home-auto-item-r1' : '',
                      isV3 ? 'home-auto-item-v3' : '',
                    ]}
                    onClick={e => {
                      // 阻止事件冒泡，防止Swiper误判为滑动
                      e.stopPropagation();
                      e.preventDefault();
                      if (isPlaceHolder) {
                        return;
                      }
                      if (item.tag == 'ai_agent') {
                        userStore.doWhenLogin(() => {
                          agentPageStore.gotoAgentChat(item.id);
                        });
                        return;
                      }
                      // 不跳转，只切换
                      switchedModelId.current = item.id
                      switchModel()
                    }}
                  >
                    <div
                      className={[
                        `size-48px flex-shrink-0 ${item?.model_type !== 4 ? 'rounded-full' : 'rounded-8px bg-white'} overflow-hidden relative`,
                        'flex justify-center',
                      ]}
                    >
                      <Image
                        preview={false}
                        src={item?.icon}
                        fallback={`https://p1.ssl.qhimg.com/t110b9a93018295fdd8f951fb2c.png`}
                        className="!size-48px !object-cover pointer-events-none"
                        rootClassName="!size-48px !object-cover"
                      />
                      {item.conversation_type == 1 ? (
                        <img
                          className="size-20px object-cover absolute left-0 bottom-0"
                          src={tti_icon}
                        />
                      ) : null}
                    </div>
                    <div className="w-full h-40px flex flex-col justify-center">
                      <div className="flex items-center justify-center text-14px font-bold text-primary-opacity text-opacity-90 leading-22px">
                        <span
                          className={`business-swiper-title w-0 flex-1 text-left line-clamp-2  ${activeTab != 10 && isPlaceHolder ? 'group-hover:hidden' : ''}`}
                        >
                          {isFullR1
                            ? 'DeepSeek 0528新版'
                            : isV3
                              ? 'DeepSeek编程超强'
                              : item.home_recommend || item.title}
                        </span>
                        {activeTab != 10 && isPlaceHolder && (
                          <div
                            className="w-76px h-32px flex justify-center items-center rounded-8px text-white bg-[#006AFF] hidden group-hover:flex hover:bg-[#005EE3]"
                            onClick={evt => {
                              // 阻止事件冒泡，防止Swiper误判为滑动
                              evt.stopPropagation();
                              evt.preventDefault();

                              if (!userStore.isUser) {
                                userStore.gotoLogin();
                                return;
                              }
                              ModelManagementModal.show({
                                logArrt: 'model_manage',
                                fromTab: activeTab,
                                id: item.firm_id || item.id,
                                item: item,
                              });
                            }}
                          >
                            配置模型
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                </Tooltip>
              </SwiperSlide>
            );
          })}
          <div
            className="mask w-62px h-full absolute top-0 right-0"
            style={{
              background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.00) 5%, #FFF 100%)',
            }}
          ></div>
        </Swiper>

        <NoneKbWarningModal open={noKbModalVisible} onConfirm={confirmSwitchModel} onCancel={() => updateNoKbModalVisible(false)}/>
      </div>
    </div>
  );
});
export default BusinessSwiper;
